import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import type { FC, Ref } from "react"

import { usePayFileColumns } from "../hooks/usePayFileColumns"

import { PayFileRowDetails } from "./PayFileRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { expandedCellStyles } from "@/components/Tables/Aeros/shared"
import type { PayFileListFieldsFragment } from "@/graphql/codegen/graphql"

interface PayFileTableProps {
  data: PayFileListFieldsFragment[]
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  expanded: ExpandedState
  onExpandedChange: OnChangeFn<ExpandedState>
  loading: boolean
  error?: { message: string }
  onScroll: () => void
  containerRef?: Ref<HTMLDivElement>
  onExport: (payFile: Pick<PayFileListFieldsFragment, "payFileId" | "referenceNumber">) => Promise<boolean>
  onSuccess: () => void
}

export const PayFileTable: FC<PayFileTableProps> = ({
  data,
  sorting,
  onSortingChange,
  expanded,
  onExpandedChange,
  loading,
  error,
  onScroll,
  containerRef,
  onExport,
  onSuccess,
}) => {
  const columns = usePayFileColumns({ onExport, onSuccess })

  const renderExpandedContent = (row: Row<PayFileListFieldsFragment>) => (
    <PayFileRowDetails payFile={row.original} onSuccess={onSuccess} />
  )

  return (
    <AerosBaseTable<PayFileListFieldsFragment>
      error={error}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
            expanded,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.payFileId,
          onSortingChange,
          onExpandedChange,
          getRowCanExpand: () => true,
          getExpandedRowModel: getExpandedRowModel(),
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: {
          ref: containerRef,
          onScroll,
          className: "overflow-auto",
          ...({ "data-testid": "pay-file.list.table" } as Record<string, string>),
        },
        bodyRow: {
          className: "hover:bg-gray-50",
        },
        bodyCellExpanded: { className: "bg-primary-100" },
        expandedCell: expandedCellStyles,
      }}
      // A failed load also clears `data`, so leaving the message unset keeps the table from
      // claiming there are no pay files alongside the error.
      noDataMessage={error ? undefined : "No pay files found"}
    />
  )
}
